// The API rejects request bodies over 5 MB (post_max_size in the backend's
// public/.htaccess). Over that limit PHP prints a warning before Laravel runs,
// so the response has no CORS header and the browser reports a "CORS error".
// The cap stays at 5 MB on purpose: marketplace feeds (Chrono24, Google,
// WhatsApp/Meta catalogue, eBay, WatchFinder) fetch /api/files/{id} as-is, and
// Meta's limit is 8 MB, eBay's 12 MB. So oversized photos are shrunk here.
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

// Headroom under the cap for the multipart envelope.
const TARGET_BYTES = MAX_UPLOAD_BYTES - 512 * 1024;
// 3000px still clears every marketplace spec (WatchFinder wants 1920px tall,
// eBay recommends 1600px) and keeps a shrunk photo around 1-2 MB, so bulk
// uploads move a third of the bytes they would at 4000px / q0.9.
const MAX_EDGE = 3000;

const toJpegBlob = (canvas, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));

/**
 * Returns `file` untouched when it is not an image or already fits; otherwise
 * a downscaled JPEG File under the upload cap. Never throws: if the browser
 * can't decode the image (e.g. HEIC on Chrome) the original is returned and
 * the server's rejection is surfaced by the caller.
 */
export async function shrinkImageForUpload(file) {
  if (!file || !file.type?.startsWith("image/") || file.size <= TARGET_BYTES) {
    return file;
  }

  try {
    const bitmap = await window.createImageBitmap(file);
    let scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    let quality = 0.85;

    for (let attempt = 0; attempt < 6; attempt++) {
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      const ctx = canvas.getContext("2d");
      // JPEG has no alpha — paint white first so transparent areas don't go black.
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

      const blob = await toJpegBlob(canvas, quality);
      if (blob && blob.size <= TARGET_BYTES) {
        bitmap.close?.();
        const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
        return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
      }

      // Alternate between dropping quality and dimensions.
      if (attempt % 2 === 0) quality -= 0.1;
      else scale *= 0.8;
    }
    bitmap.close?.();
  } catch (e) {
    // fall through to the original file
  }

  return file;
}
